import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from 'app/api';
import { setSnackbar } from './snackbarSlice';
import { getSiteUsers, deleteSite, runInBatches, bulkSnackbar } from './siteSlice';
import { getAllUsersByOrgId } from './orgSlice';

// status: 'active' | 'inactive' | 'all' (GetUsers defaults to active when omitted).
export const getUserList = createAsyncThunk(
  'user/getList',
  async ({ page = 1, pageSize = 50, search = '', status } = {}, { dispatch, rejectWithValue }) => {
    try {
      const params = { page, pageSize };
      if (status) params.status = status;
      const trimmedSearch = typeof search === 'string' ? search.trim() : '';
      if (trimmedSearch) params.search = trimmedSearch;

      const res = await API.get('/GetUsers', { params });
      const data = res?.data;
      // GetUsers now returns an envelope ({ data, totalCount, ... }); fall back
      // to a bare array for safety in case an older API build responds.
      const users = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];

      const pagination = data && !Array.isArray(data) && typeof data === 'object'
        ? {
            page: Number.isFinite(data.page) ? data.page : page,
            pageSize: Number.isFinite(data.pageSize) ? data.pageSize : pageSize,
            totalCount: Number.isFinite(data.totalCount) ? data.totalCount : users.length,
            totalPages: Number.isFinite(data.totalPages) ? data.totalPages : Math.max(1, Math.ceil(users.length / pageSize)),
            hasPreviousPage: typeof data.hasPreviousPage === 'boolean' ? data.hasPreviousPage : page > 1,
            hasNextPage: typeof data.hasNextPage === 'boolean' ? data.hasNextPage : false,
          }
        : { page: 1, pageSize: users.length, totalCount: users.length, totalPages: 1, hasPreviousPage: false, hasNextPage: false };

      return { users, pagination };
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: 'Unable to retrieve user info' }));
      return rejectWithValue(error.message);
    }
  }
);

export const getUserSites = createAsyncThunk(
  'user/getSites',
  async (userId, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await API.get('/GetUserSitesByUserId', { params: { UserId: userId } });
      return data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: 'Unable to retrieve Projects info' }));
      return rejectWithValue(error.message);
    }
  }
);

// Admin user dialog: another user's orgs and projects. Not stored in the slice, because
// userSites/userOrgs hold the signed-in user's own memberships.
export const getUserMemberships = createAsyncThunk(
  'user/getMemberships',
  async (userId, { dispatch, rejectWithValue }) => {
    try {
      const [sites, orgs] = await Promise.all([
        API.get('/GetUserSitesByUserId', { params: { UserId: userId } }),
        API.get('/GetAllOrgsByUserID', { params: { UserId: userId } }),
      ]);
      return {
        sites: Array.isArray(sites.data) ? sites.data : [],
        orgs: Array.isArray(orgs.data) ? orgs.data : [],
      };
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: 'Unable to retrieve user memberships' }));
      return rejectWithValue(error.message);
    }
  }
);

export const getAllOrgsByUserId = createAsyncThunk(
  'user/getOrgs',
  async (userId, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await API.get('/GetAllOrgsByUserID', { params: { UserId: userId } });
      return data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: 'Unable to retrieve org info' }));
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserSiteRole = createAsyncThunk(
  'user/updateSiteRole',
  async ({ siteId, userId, email, updatedBy, siteRole }, { dispatch, rejectWithValue }) => {
    try {
      const res = await API.put('/UpdateUserSiteRole', {
        SiteId: siteId,
        UserId: userId,
        UserEmail: email,
        UpdatedBy: updatedBy,
        SiteRole: siteRole,
      });
      dispatch(getSiteUsers({ siteId }));
      dispatch(setSnackbar({ status: 'success', message: 'User role updated successfully' }));
      return res.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: `Received status code ${error}` }));
      return rejectWithValue(error.message);
    }
  }
);

export const removeUserFromSite = createAsyncThunk(
  'user/removeFromSite',
  async ({ siteId, userToDelete }, { dispatch, rejectWithValue }) => {
    try {
      const res = await API.delete('/RemoveUserFromSite', {
        params: { SiteId: siteId, UserEmail: userToDelete },
      });
      dispatch(getSiteUsers({ siteId }));
      dispatch(setSnackbar({ status: 'success', message: 'User removed successfully' }));
      return res.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: `Received status code ${error}` }));
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserAvatar = createAsyncThunk(
  'user/updateAvatar',
  async ({ userId, photo }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', photo);
      const res = await API.post('/UploadUserPhoto', formData, {
        params: { UserId: userId },
      });
      return res.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateUser = createAsyncThunk(
  'user/update',
  async (user, { dispatch, rejectWithValue }) => {
    try {
      const res = await API.put('/UpdateUser', user, {
        params: { UserId: user.id },
      });
      dispatch(setSnackbar({ status: 'success', message: 'User updated successfully' }));
      return res.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: 'Unable to update user' }));
      return rejectWithValue(error.message);
    }
  }
);

export const deactivateUser = createAsyncThunk(
  'user/deactivate',
  async (userEmail, { dispatch, rejectWithValue }) => {
    try {
      const res = await API.delete('/DeleteUser', { params: { Email: userEmail } });
      dispatch(setSnackbar({ status: 'success', message: 'User deactivated' }));
      return res.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: 'Unable to deactivate user' }));
      return rejectWithValue(error.message);
    }
  }
);

export const reactivateUser = createAsyncThunk(
  'user/reactivate',
  async (userEmail, { dispatch, rejectWithValue }) => {
    try {
      const res = await API.post('/ReactivateUser', null, { params: { Email: userEmail } });
      dispatch(setSnackbar({ status: 'success', message: 'User activated' }));
      return res.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: 'Unable to activate user' }));
      return rejectWithValue(error.message);
    }
  }
);

// Admin bulk actions for the Users page. Results are keyed by email, which is
// what DeleteUser / ReactivateUser / InviteUserToOrg take.
export const bulkSetUsersActive = createAsyncThunk(
  'user/bulkSetActive',
  async ({ emails, isActive }, { dispatch }) => {
    const result = await runInBatches(emails, (email) =>
      isActive
        ? API.post('/ReactivateUser', null, { params: { Email: email } })
        : API.delete('/DeleteUser', { params: { Email: email } })
    );
    bulkSnackbar(dispatch, result, isActive ? 'Activated' : 'Deactivated', 'user');
    return result;
  }
);

export const bulkAddUsersToOrg = createAsyncThunk(
  'user/bulkAddToOrg',
  async ({ emails, orgId, orgRoleId }, { dispatch }) => {
    const result = await runInBatches(emails, (email) =>
      API.post('/InviteUserToOrg', { userId: '', userEmail: email, orgId, orgRoleId })
    );
    bulkSnackbar(dispatch, result, 'Added', 'user');
    return result;
  }
);

export const inviteUserToOrg = createAsyncThunk(
  'user/inviteToOrg',
  async ({ orgId, userEmail, orgRoleId, userId = '' }, { dispatch, rejectWithValue }) => {
    try {
      const response = await API.post('/InviteUserToOrg', { userId, userEmail, orgId, orgRoleId });
      dispatch(setSnackbar({ status: 'success', message: 'User Added to Organization' }));
      return response.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: `Received status code ${error.message}` }));
      return rejectWithValue(error.message);
    }
  }
);

export const removeUserFromOrg = createAsyncThunk(
  'user/removeFromOrg',
  async ({ orgId, userId, userEmail }, { dispatch, rejectWithValue }) => {
    try {
      const response = await API.delete('/RemoveUserFromOrg', { data: { userId, userEmail, orgId } });
      dispatch(getAllUsersByOrgId(orgId));
      dispatch(setSnackbar({ status: 'success', message: 'User removed successfully' }));
      return response.data;
    } catch (error) {
      // 409 carries a readable reason (e.g. the org's last owner cannot leave); show it as-is.
      const serverMessage = typeof error.response?.data === 'string' ? error.response.data : null;
      dispatch(setSnackbar({ status: 'error', message: serverMessage || `Received status code ${error}` }));
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserOrgRole = createAsyncThunk(
  'user/updateOrgRole',
  async ({ orgId, userId, userEmail, orgRoleId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await API.put('/UpdateUserOrgRole', { userId, userEmail, orgId, orgRoleId });
      dispatch(getAllUsersByOrgId(orgId));
      dispatch(setSnackbar({ status: 'success', message: 'User role updated successfully' }));
      return response.data;
    } catch (error) {
      dispatch(setSnackbar({ status: 'error', message: `Received status code ${error}` }));
      return rejectWithValue(error.message);
    }
  }
);

const setUserActive = (state, email, isActive) => {
  const target = String(email ?? '').toLowerCase();
  state.userList.forEach((u) => {
    if (String(u.email ?? '').toLowerCase() === target) u.isActive = isActive;
  });
};

const userSlice = createSlice({
  name: 'users',
  initialState: {
    user: {},
    userList: [],
    userListPagination: {
      page: 1,
      pageSize: 50,
      totalCount: 0,
      totalPages: 1,
      hasPreviousPage: false,
      hasNextPage: false,
    },
    userSites: [],
    userOrgs: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearUserList: (state) => {
      state.userList = [];
    },
    clearUserSites: (state) => {
      state.userSites = [];
    },
    clearUserOrgs: (state) => {
      state.userOrgs = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserList.fulfilled, (state, action) => {
        state.userList = action.payload.users;
        state.userListPagination = action.payload.pagination;
      })
      // Flip the row in place so the admin keeps their page, search, and filter.
      .addCase(deactivateUser.fulfilled, (state, action) => {
        setUserActive(state, action.meta.arg, false);
      })
      .addCase(reactivateUser.fulfilled, (state, action) => {
        setUserActive(state, action.meta.arg, true);
      })
      .addCase(bulkSetUsersActive.fulfilled, (state, action) => {
        action.payload.succeeded.forEach((email) =>
          setUserActive(state, email, action.meta.arg.isActive)
        );
      })
      .addCase(getUserSites.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserSites.fulfilled, (state, action) => {
        state.userSites = action.payload;
        state.loading = false;
      })
      .addCase(getUserSites.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteSite.fulfilled, (state, action) => {
        const removedId = Number(action.meta.arg?.siteId);
        state.userSites = state.userSites.filter((site) => site.id !== removedId);
      })
      .addCase(getAllOrgsByUserId.fulfilled, (state, action) => {
        state.userOrgs = action.payload;
      });
  },
});

export const { clearUserList, clearUserSites, clearUserOrgs } = userSlice.actions;
export default userSlice.reducer;
